import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useAuth } from "@clerk/clerk-react"
import { useHistory } from "react-router-dom"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { Unlock } from "lucide-react"
import { Box, Flex, HStack, Stack, Text } from "@chakra-ui/react"

import Card from "@/components/Card/Card"
import CardBody from "@/components/Card/CardBody"
import CardHeader from "@/components/Card/CardHeader"
import { ReportFindingsDisclaimerBox } from "@/components/Reports/ReportFindingsDisclaimerBox"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { LockIcon } from "@/components/ui/lock-icon"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { cn } from "@/lib/utils"
import { useIsMobile } from "@/hooks/use-mobile"
import { resolveDemoCohortTrades } from "@/services/demoEntryAnalysisApi"
import { trackDemoEntryAnalysisEvent } from "@/utils/demoEntryAnalysisAnalytics"
import { demoPayloadToReport } from "@/utils/demoEntryAnalysisReport"
import { getReportCohortWindowLabel } from "@/utils/reportListDisplay"
import {
  DEMO_ENTRY_ANALYSIS_BASE_PATH,
  readDemoSession,
} from "@/utils/demoEntryAnalysisSession"
import { redirectToBillingPaywall } from "@/utils/billingPaywall"
import { DemoResultsPricingSection } from "./DemoResultsPricingSection"
import { DemoSimulatorUpsellPanel } from "./DemoSimulatorUpsellPanel"
import {
  backendTradeStatisticsToFrontend,
  extractReportWindowTradeStatistics,
} from "@/utils/extractReportWindowTradeStatistics"
import type { MarketContextNavigationTarget } from "@/utils/marketContextNavigation"
import type {
  ConditionAnalysisBucketRow,
  ConditionAnalysisScope,
  ConditionAnalysisSituationRow,
  ConditionAnalysisSlice,
  ConditionAnalysisSummaryRow,
} from "@/utils/conditionAnalysisSelectors"
import type { CohortPreviewStats } from "@/utils/cohortTradesResolve"
import type { ReportPickSliceContext } from "@/utils/reportPickListStorage"
import {
  formatFindingConfidence,
  parseReportFindings,
} from "@/utils/reportFindings"
import { ConditionAnalysisSection } from "@/views/Dashboard/Reports/ConditionAnalysisSection"
import {
  REPORT_DETAIL_CARD_BORDER,
  buildReportDetailMetaItems,
  reportDetailSectionCardProps,
  ReportsCardMetaRowWithBadges,
} from "@/views/Dashboard/Reports/components/ReportsCardChrome"
import {
  ReportMarketCohortPreviewCard,
  upsertCohortOverlayStats,
} from "@/views/Dashboard/Reports/components/ReportMarketCohortPreviewCard"
import { ReportEvidencePointersBlock } from "@/views/Dashboard/Reports/ReportEvidencePointersBlock"
import { useMarketCohortPreview } from "@/views/Dashboard/Reports/useMarketCohortPreview"
import {
  REPORTS_DETAIL_FIELD_DESC_CLASS,
  REPORTS_DETAIL_FINDING_META_LABEL_CLASS,
  useReportsDetailColors,
} from "@/views/Dashboard/Reports/reportsDetailTheme"
import OverviewMetrics from "@/views/Dashboard/Statistics/components/OverviewMetrics"
import {
  AUTH_FONT,
  AUTH_TROPICAL,
  DemoEntryAnalysisShell,
  DemoLoadingBlock,
  WIZARD_DESC_CLASS,
} from "./DemoEntryAnalysisShell"

const REPORT_PRIMARY_FONT = "var(--font-primary)"

const REPORT_OVERVIEW_FIELD_LABEL_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-[color:var(--brand-tropical-surf)]"

const FINDING_ACCORDION_FIELD_LABEL_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)]"

const FINDING_META_BADGE_BASE =
  "border-0 bg-transparent font-[family-name:var(--font-primary)] font-medium normal-case tracking-normal text-white shadow-none"

const FINDING_META_BADGE_CONFIDENCE_CLASS = `${FINDING_META_BADGE_BASE} bg-[image:linear-gradient(135deg,#38BBD1,#3EEAEC)]`
const FINDING_META_BADGE_TYPE_CLASS = `${FINDING_META_BADGE_BASE} bg-[image:linear-gradient(135deg,#6B7280,#9CA3AF)]`

function FindingHypothesisBlock(props: {
  hypothesis: string
  subtleText: string
  previewClamped: boolean
}): React.ReactElement {
  return (
    <Box w="100%" minW={0}>
      <Text fontSize="xs" color="#38BBD1" mb="4px" fontFamily={REPORT_PRIMARY_FONT}>
        Hypothesis
      </Text>
      <Text
        fontSize="sm"
        color={props.subtleText}
        lineHeight="1.5"
        fontFamily={REPORT_PRIMARY_FONT}
        noOfLines={props.previewClamped ? 4 : undefined}
      >
        {props.hypothesis}
      </Text>
    </Box>
  )
}

export default function DemoResultsPage(): React.ReactElement {
  const history = useHistory()
  const { isLoaded, isSignedIn } = useAuth()
  const { subtleText, textColor, evidencePreBorder, isDark } = useReportsDetailColors()
  const evidencePreBg = isDark ? "whiteAlpha.100" : "gray.50"

  const session = readDemoSession()
  const [expandedFindingId, setExpandedFindingId] = useState<string | null>(null)
  const [sectionTab, setSectionTab] = useState("overview")
  const [marketContextFocus, setMarketContextFocus] =
    useState<MarketContextNavigationTarget | null>(null)
  const [marketContextFocusNonce, setMarketContextFocusNonce] = useState(0)
  const isMobile = useIsMobile()
  const summarySectionRef = useRef<HTMLDivElement | null>(null)
  const pricingSectionRef = useRef<HTMLElement | null>(null)
  const [showMobileUpgradeBanner, setShowMobileUpgradeBanner] = useState(false)

  const scrollToPricingSection = useCallback(() => {
    trackDemoEntryAnalysisEvent("subscribe_nudge_clicked", {
      surface: "mobile_sticky_banner",
    })
    pricingSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
  }, [])

  const payload = session?.readyPayload
  const demoJobId = session?.jobId?.trim() || ""
  const canPreviewTrades = Boolean(demoJobId && demoJobId !== "sample")

  const report = useMemo(
    () => (payload ? demoPayloadToReport(payload) : null),
    [payload]
  )
  const findings = useMemo(() => (report ? parseReportFindings(report) : []), [report])

  const marketCohortPreview = useMarketCohortPreview(canPreviewTrades ? demoJobId : "", {
    resolvePage: canPreviewTrades ? resolveDemoCohortTrades : undefined,
  })
  const [marketCohortOverlayStatsByKey, setMarketCohortOverlayStatsByKey] = useState<
    Record<string, CohortPreviewStats | null>
  >({})

  useEffect(() => {
    const activeKeys = new Set(marketCohortPreview.previews.map((p) => p.rowKey))
    setMarketCohortOverlayStatsByKey((prev) => {
      let changed = false
      const next: Record<string, CohortPreviewStats | null> = {}
      for (const [key, value] of Object.entries(prev)) {
        if (!activeKeys.has(key)) {
          changed = true
          continue
        }
        next[key] = value
      }
      return changed ? next : prev
    })
  }, [marketCohortPreview.previews])

  const onUpsertCohortOverlayStats = useCallback(
    (rowKey: string, nextStats: CohortPreviewStats | null) => {
      setMarketCohortOverlayStatsByKey((prev) => upsertCohortOverlayStats(prev, rowKey, nextStats))
    },
    []
  )

  const onPreviewConditionBucket = useCallback(
    (
      row: ConditionAnalysisBucketRow,
      sliceContext: ReportPickSliceContext | null | undefined,
      analysisScope: ConditionAnalysisScope | null | undefined,
      label?: string
    ) => {
      marketCohortPreview.previewConditionBucket(row, sliceContext, analysisScope, label)
    },
    [marketCohortPreview.previewConditionBucket]
  )
  const onPreviewConditionSituation = useCallback(
    (
      row: ConditionAnalysisSituationRow,
      sliceContext: ReportPickSliceContext | null | undefined,
      analysisScope: ConditionAnalysisScope | null | undefined,
      label?: string
    ) => {
      marketCohortPreview.previewConditionSituation(row, sliceContext, analysisScope, label)
    },
    [marketCohortPreview.previewConditionSituation]
  )
  const onPreviewConditionSlice = useCallback(
    (
      slice: ConditionAnalysisSlice,
      sliceContext: ReportPickSliceContext | null | undefined,
      label: string,
      expectedSampleSize?: number
    ) => {
      marketCohortPreview.previewConditionSlice(slice, sliceContext, label, expectedSampleSize)
    },
    [marketCohortPreview.previewConditionSlice]
  )
  const onPreviewConditionSummaryRow = useCallback(
    (
      tableKey: string,
      row: ConditionAnalysisSummaryRow,
      sliceContext: ReportPickSliceContext | null | undefined,
      label: string
    ) => {
      marketCohortPreview.previewConditionSummaryRow(
        tableKey,
        row,
        sliceContext,
        label,
        row.sample_size
      )
    },
    [marketCohortPreview.previewConditionSummaryRow]
  )

  const reportMetaItems = useMemo(() => {
    if (!report) return []
    const dateLabel = getReportCohortWindowLabel(report)
    return buildReportDetailMetaItems({
      dateLabel,
      candleTimeframe: report.candle_timeframe || "1h",
    })
  }, [report])

  const reportOverviewMetricsBundle = useMemo(() => {
    const backend = extractReportWindowTradeStatistics(report?.input_snapshot)
    if (!backend) return null
    return {
      backend,
      statistics: backendTradeStatisticsToFrontend(backend),
    }
  }, [report?.input_snapshot])

  useEffect(() => {
    if (!isMobile) {
      setShowMobileUpgradeBanner(false)
      return
    }

    const updateBannerVisibility = () => {
      const summary = summarySectionRef.current
      const pricing = pricingSectionRef.current
      if (!summary || !pricing) {
        setShowMobileUpgradeBanner(false)
        return
      }

      const summaryRect = summary.getBoundingClientRect()
      const pricingRect = pricing.getBoundingClientRect()
      const pastSummary = summaryRect.bottom <= 16
      const pricingInView = pricingRect.top < window.innerHeight * 0.88

      setShowMobileUpgradeBanner(pastSummary && !pricingInView)
    }

    updateBannerVisibility()
    window.addEventListener("scroll", updateBannerVisibility, { passive: true })
    window.addEventListener("resize", updateBannerVisibility)

    return () => {
      window.removeEventListener("scroll", updateBannerVisibility)
      window.removeEventListener("resize", updateBannerVisibility)
    }
  }, [isMobile, reportOverviewMetricsBundle, sectionTab])

  const onOpenMarketContext = useCallback((target: MarketContextNavigationTarget) => {
    if (typeof document !== "undefined" && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
    setMarketContextFocus(target)
    setMarketContextFocusNonce((n) => n + 1)
    setSectionTab("market")
  }, [])

  const candleTimeframeForReview = useMemo(():
    | "1m"
    | "5m"
    | "15m"
    | "30m"
    | "1h"
    | "4h"
    | null => {
    const tf = String(report?.candle_timeframe || "1h").toLowerCase()
    if (
      tf === "1m" ||
      tf === "5m" ||
      tf === "15m" ||
      tf === "30m" ||
      tf === "1h" ||
      tf === "4h"
    ) {
      return tf
    }
    return "1h"
  }, [report?.candle_timeframe])
  useEffect(() => {
    if (!isLoaded) return
    if (!isSignedIn) {
      history.replace(`${DEMO_ENTRY_ANALYSIS_BASE_PATH}/generating?claim=1`)
      return
    }
    if (!payload) return
    trackDemoEntryAnalysisEvent("results_viewed", {
      symbol: report?.symbol || "",
      findings: findings.length,
    })
  }, [findings.length, history, isLoaded, isSignedIn, payload, report?.symbol])

  if (!isLoaded) {
    return (
      <DemoEntryAnalysisShell layout="compact">
        <DemoLoadingBlock centered />
      </DemoEntryAnalysisShell>
    )
  }

  if (!payload || !report) {
    return (
      <DemoEntryAnalysisShell layout="compact">
        <div
          className="flex flex-col items-center justify-center gap-6 px-2 py-8 text-center sm:py-10"
          data-demo-expired-state=""
        >
          <p className={cn(WIZARD_DESC_CLASS, "text-sm")}>
            Your demo report may have expired.
          </p>
          <Button
            type="button"
            variant="gradient"
            onClick={() => history.push(DEMO_ENTRY_ANALYSIS_BASE_PATH)}
          >
            Create report
          </Button>
        </div>
      </DemoEntryAnalysisShell>
    )
  }

  const narrativeForDisplay = String(report.narrative || "-")

  return (
    <>
    <DemoEntryAnalysisShell
      title={report.title || "Entry Analysis"}
      titleMeta={
        <div data-reports-card-chrome="" className="w-full min-w-0">
          <ReportsCardMetaRowWithBadges
            metaItems={reportMetaItems}
            reportType={report.report_type}
            reportTypeLabel="Entry Analysis"
            symbol={report.symbol}
          />
        </div>
      }
    >
      <div
        className={cn(
          "flex flex-col gap-6",
          showMobileUpgradeBanner && "pb-[calc(5.5rem+env(safe-area-inset-bottom))] md:pb-0"
        )}
      >
        <Alert variant="info">
          <AlertDescription
            className={cn(
              AUTH_FONT,
              // Mobile: centered stack — copy, then Upgrade (with unlock icon).
              // sm+: lock + copy left, Upgrade right.
              "!text-muted-foreground flex flex-col items-center gap-3 text-center sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:text-left"
            )}
          >
            <div className="flex min-w-0 flex-col items-center gap-3 sm:flex-row sm:items-center sm:gap-2.5">
              <div
                className="hidden shrink-0 items-center justify-center rounded-[6px] border border-[color-mix(in_oklab,var(--brand-tropical-surf)_45%,transparent)] bg-white/70 p-1.5 sm:flex"
                aria-hidden
              >
                <LockIcon
                  size={18}
                  className="text-[color:var(--brand-tropical-surf)] [&_svg]:!size-[18px]"
                />
              </div>
              <span className="min-w-0">
                You can explore Overview and Market. Simulator and deeper tools unlock with a
                subscription.
              </span>
            </div>
            <Button
              type="button"
              variant="gradient"
              size="sm"
              className="shrink-0"
              onClick={() => {
                trackDemoEntryAnalysisEvent("subscribe_nudge_clicked", {
                  surface: "overview_market_info_banner",
                })
                redirectToBillingPaywall()
              }}
            >
              <Unlock className="size-4 sm:hidden" aria-hidden />
              Upgrade
            </Button>
          </AlertDescription>
        </Alert>

        <Tabs
          data-trade-log-tabs=""
          value={sectionTab}
          onValueChange={setSectionTab}
          className="w-full"
        >
          <div className="w-full overflow-x-auto overflow-y-hidden">
            <TabsList className="min-w-max">
              <TabsTrigger value="overview" title="Overview">
                Overview
              </TabsTrigger>
              <TabsTrigger value="market" title="Entry Analysis">
                Market
              </TabsTrigger>
              <TabsTrigger value="simulator" title="Simulator">
                Simulator
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent
            value="overview"
            className="mt-0 data-[state=inactive]:hidden"
          >
            <Box data-report-overview="" display="flex" flexDirection="column" alignItems="stretch" w="100%">
              <Card {...reportDetailSectionCardProps}>
                <CardHeader pb={{ base: 3, md: 4 }}>
                  <Box flex="1" minW={{ base: "100%", md: "260px" }}>
                    <Text
                      fontSize={{ base: "2xl", md: "3xl" }}
                      color="#38BBD1"
                      fontWeight="normal"
                      fontFamily={REPORT_PRIMARY_FONT}
                      lineHeight="1.15"
                    >
                      Overview
                    </Text>
                    <Text fontSize="sm" color={subtleText} mt={1} fontFamily={REPORT_PRIMARY_FONT}>
                      Report narrative, structured findings, and evidence for this run.
                    </Text>
                  </Box>
                </CardHeader>
                <CardBody display="flex" flexDirection="column" pt={0} w="full">
                  <Stack w="100%" spacing={{ base: 5, md: 6 }}>
                    <Box minW={0} w="100%">
                      <Field className="w-full">
                        <FieldLabel
                          htmlFor="demo-report-overview-narrative"
                          className={REPORT_OVERVIEW_FIELD_LABEL_CLASS}
                        >
                          Narrative
                        </FieldLabel>
                        <FieldDescription className={REPORTS_DETAIL_FIELD_DESC_CLASS}>
                          Written analysis for this report run.
                        </FieldDescription>
                        <Box
                          id="demo-report-overview-narrative"
                          data-report-narrative-body=""
                          flexShrink={0}
                          fontSize="sm"
                          fontWeight="normal"
                          borderWidth="1px"
                          borderStyle="solid"
                          borderColor={REPORT_DETAIL_CARD_BORDER}
                          borderRadius="6px"
                          p="12px"
                          color={subtleText}
                          w="100%"
                          maxH={{ base: "none", md: "min(78vh, 900px)" }}
                          overflowY={{ base: "visible", md: "auto" }}
                          fontFamily={REPORT_PRIMARY_FONT}
                          sx={{
                            borderColor: `${REPORT_DETAIL_CARD_BORDER} !important`,
                            "& a": { color: "cyan.400" },
                            "& p": {
                              marginTop: "0",
                              marginBottom: "10px",
                              fontFamily: REPORT_PRIMARY_FONT,
                            },
                            "& ul, & ol": {
                              paddingLeft: "20px",
                              marginTop: "0",
                              marginBottom: "10px",
                            },
                            "& code": {
                              fontFamily: "mono",
                              fontSize: "0.9em",
                              background: "rgba(0,0,0,0.06)",
                              padding: "2px 6px",
                              borderRadius: "6px",
                              _dark: { background: "rgba(255,255,255,0.06)" },
                            },
                            "& pre": {
                              overflow: "auto",
                              padding: "10px",
                              borderRadius: "6px",
                              background: "rgba(0,0,0,0.06)",
                              _dark: { background: "rgba(255,255,255,0.06)" },
                            },
                            "& pre code": { background: "transparent", padding: 0 },
                            "& strong": {
                              color: "#38BBD1",
                              fontFamily: REPORT_PRIMARY_FONT,
                              fontWeight: 700,
                            },
                            "& blockquote": {
                              borderLeft: "3px solid",
                              borderColor: "cyan.300",
                              paddingLeft: "10px",
                              color: subtleText,
                            },
                          }}
                        >
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {narrativeForDisplay}
                          </ReactMarkdown>
                        </Box>
                        <ReportFindingsDisclaimerBox />
                      </Field>
                    </Box>

                    <Box>
                      <Field className="w-full">
                        <FieldLabel
                          htmlFor="demo-report-overview-findings"
                          data-report-findings-heading=""
                          className={REPORT_OVERVIEW_FIELD_LABEL_CLASS}
                        >
                          Findings
                        </FieldLabel>
                        <FieldDescription className={REPORTS_DETAIL_FIELD_DESC_CLASS}>
                          Structured recommendations with assumptions and supporting evidence.
                        </FieldDescription>
                        {findings.length > 0 ? (
                          <Box id="demo-report-overview-findings" data-report-findings-rail="" mt={2}>
                            <Box data-report-findings-accordion="" borderRadius="6px" overflow="hidden">
                              <Accordion
                                type="single"
                                collapsible
                                value={expandedFindingId ?? ""}
                                onValueChange={(value) => setExpandedFindingId(value || null)}
                                className="space-y-2 border-0"
                              >
                                {findings.map((rec, idx) => {
                                  const recId = rec.id || `rec_${idx}`
                                  const findingTitle = rec.title || `Finding ${idx + 1}`
                                  const findingExpanded = expandedFindingId === recId
                                  const confidenceLabel = formatFindingConfidence(rec.confidence)
                                  return (
                                    <AccordionItem
                                      key={recId}
                                      value={recId}
                                      data-report-finding-card
                                      data-report-finding-accordion-item
                                      className="rounded-lg border border-border bg-card px-3 not-last:border-b dark:bg-sidebar"
                                    >
                                      <AccordionTrigger
                                        className="items-start py-3 font-medium hover:no-underline"
                                        aria-label={
                                          findingExpanded
                                            ? `Collapse finding: ${findingTitle}`
                                            : `Expand finding: ${findingTitle}`
                                        }
                                      >
                                        <div className="flex w-full min-w-0 flex-col gap-2 pr-2 text-left">
                                          {(confidenceLabel || rec.action_type) && (
                                            <Flex
                                              data-report-finding-meta=""
                                              flexWrap="nowrap"
                                              align="center"
                                              columnGap={{ base: 2, md: 3 }}
                                              rowGap={2}
                                              justify="flex-start"
                                              overflowX="auto"
                                              whiteSpace="nowrap"
                                              minW={0}
                                              onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                            >
                                              {confidenceLabel ? (
                                                <HStack spacing={2} align="center" flexShrink={0}>
                                                  <span
                                                    data-report-finding-meta-label=""
                                                    className={REPORTS_DETAIL_FINDING_META_LABEL_CLASS}
                                                  >
                                                    Confidence
                                                  </span>
                                                  <Badge
                                                    data-report-finding-meta-badge="confidence"
                                                    className={FINDING_META_BADGE_CONFIDENCE_CLASS}
                                                  >
                                                    {confidenceLabel}
                                                  </Badge>
                                                </HStack>
                                              ) : null}
                                              {rec.action_type ? (
                                                <HStack
                                                  spacing={2}
                                                  align="center"
                                                  flexShrink={0}
                                                  maxW="100%"
                                                >
                                                  <span
                                                    data-report-finding-meta-label=""
                                                    className={REPORTS_DETAIL_FINDING_META_LABEL_CLASS}
                                                  >
                                                    Type
                                                  </span>
                                                  <Badge
                                                    data-report-finding-meta-badge="type"
                                                    className={`${FINDING_META_BADGE_TYPE_CLASS} whitespace-nowrap`}
                                                  >
                                                    {String(rec.action_type).replace(/_/g, " ")}
                                                  </Badge>
                                                </HStack>
                                              ) : null}
                                            </Flex>
                                          )}
                                          <Field className="gap-1">
                                            <FieldLabel
                                              className={FINDING_ACCORDION_FIELD_LABEL_CLASS}
                                              title={findingTitle}
                                            >
                                              {findingTitle}
                                            </FieldLabel>
                                          </Field>
                                          {rec.hypothesis ? (
                                            <FindingHypothesisBlock
                                              hypothesis={rec.hypothesis}
                                              subtleText={subtleText}
                                              previewClamped={!findingExpanded}
                                            />
                                          ) : null}
                                        </div>
                                      </AccordionTrigger>
                                      <AccordionContent className="text-muted-foreground pt-1 pb-4">
                                        <Box id={`finding-panel-${recId}`}>
                                          {rec.analysis ? (
                                            <>
                                              <Text
                                                fontSize="xs"
                                                color="#38BBD1"
                                                mb="4px"
                                                fontFamily={REPORT_PRIMARY_FONT}
                                              >
                                                Analysis
                                              </Text>
                                              <Text
                                                fontSize="sm"
                                                color={subtleText}
                                                mb="10px"
                                                lineHeight="1.5"
                                                fontFamily={REPORT_PRIMARY_FONT}
                                              >
                                                {rec.analysis}
                                              </Text>
                                            </>
                                          ) : null}
                                          {rec.risk ? (
                                            <>
                                              <Text
                                                fontSize="xs"
                                                color="#38BBD1"
                                                mb="4px"
                                                fontFamily={REPORT_PRIMARY_FONT}
                                              >
                                                Risk if misapplied
                                              </Text>
                                              <Text
                                                fontSize="sm"
                                                color={subtleText}
                                                mb="10px"
                                                lineHeight="1.5"
                                                fontFamily={REPORT_PRIMARY_FONT}
                                              >
                                                {rec.risk}
                                              </Text>
                                            </>
                                          ) : null}
                                          {rec.validation ? (
                                            <>
                                              <Text
                                                fontSize="xs"
                                                color="#38BBD1"
                                                mb="4px"
                                                fontFamily={REPORT_PRIMARY_FONT}
                                              >
                                                How you could validate
                                              </Text>
                                              <Text
                                                fontSize="sm"
                                                color={subtleText}
                                                mb="10px"
                                                lineHeight="1.5"
                                                fontFamily={REPORT_PRIMARY_FONT}
                                              >
                                                {rec.validation}
                                              </Text>
                                            </>
                                          ) : null}
                                          {rec.evidence_pointers.length > 0 ||
                                          rec.primary_slice_id ? (
                                            <ReportEvidencePointersBlock
                                              pointers={rec.evidence_pointers}
                                              primarySliceId={rec.primary_slice_id}
                                              inputSnapshot={report.input_snapshot}
                                              analysisEvidence={report.analysis_evidence}
                                              evidencePreBg={evidencePreBg}
                                              evidencePreBorder={evidencePreBorder}
                                              subtleText={subtleText}
                                              onOpenMarketContext={onOpenMarketContext}
                                            />
                                          ) : null}
                                        </Box>
                                      </AccordionContent>
                                    </AccordionItem>
                                  )
                                })}
                              </Accordion>
                            </Box>
                          </Box>
                        ) : (
                          <Box
                            id="demo-report-overview-findings"
                            data-report-findings-rail=""
                            borderWidth="1px"
                            borderStyle="solid"
                            borderColor={REPORT_DETAIL_CARD_BORDER}
                            borderRadius="6px"
                            p="12px"
                            mt={2}
                          >
                            <Text fontSize="sm" color={subtleText} fontFamily={REPORT_PRIMARY_FONT}>
                              No structured findings were returned for this report.
                            </Text>
                          </Box>
                        )}
                      </Field>
                    </Box>

                    {reportOverviewMetricsBundle ? (
                      <Field className="w-full pt-4 md:pt-6" data-report-overview-metrics="">
                        <FieldLabel
                          htmlFor="demo-report-overview-metrics"
                          className={REPORT_OVERVIEW_FIELD_LABEL_CLASS}
                        >
                          Report metrics
                        </FieldLabel>
                        <FieldDescription className={REPORTS_DETAIL_FIELD_DESC_CLASS}>
                          Key trade statistics for this report&apos;s analysis window.
                        </FieldDescription>
                        <Box
                          ref={summarySectionRef}
                          data-demo-results-summary-section=""
                          id="demo-report-overview-metrics"
                          w="100%"
                          mt={2}
                        >
                          <OverviewMetrics
                            embedded
                            statistics={reportOverviewMetricsBundle.statistics}
                            backendStats={reportOverviewMetricsBundle.backend}
                            comparisonType="period-over-period"
                            expectancyValue={
                              (reportOverviewMetricsBundle.backend as { expectancy?: number | null })
                                ?.expectancy ??
                              reportOverviewMetricsBundle.backend.average_per_trade_pnl ??
                              null
                            }
                          />
                        </Box>
                      </Field>
                    ) : (
                      <Box
                        ref={summarySectionRef}
                        data-demo-results-summary-section=""
                        w="100%"
                        h="1px"
                        aria-hidden
                      />
                    )}
                  </Stack>
                </CardBody>
              </Card>
            </Box>
          </TabsContent>

          <TabsContent value="market" className="mt-2 data-[state=inactive]:hidden">
            <ConditionAnalysisSection
              reportType={report.report_type}
              candleTimeframe={report.candle_timeframe}
              inputSnapshot={report.input_snapshot}
              analysisEvidence={report.analysis_evidence}
              textColor={textColor}
              subtleText={subtleText}
              evidencePreBg={evidencePreBg}
              evidencePreBorder={evidencePreBorder}
              simulatorPicksDisabled
              simulatorPicksDisabledReason="Simulator picks unlock with a Tessera subscription."
              hideSliceTableCollapseControls
              marketContextFocus={marketContextFocus}
              marketContextFocusNonce={marketContextFocusNonce}
              onDismissMarketContextFocus={() => setMarketContextFocus(null)}
              onPreviewConditionBucket={
                canPreviewTrades ? onPreviewConditionBucket : undefined
              }
              onPreviewConditionSituation={
                canPreviewTrades ? onPreviewConditionSituation : undefined
              }
              onPreviewConditionSlice={canPreviewTrades ? onPreviewConditionSlice : undefined}
              onPreviewConditionSummaryRow={
                canPreviewTrades ? onPreviewConditionSummaryRow : undefined
              }
              previewActiveKey={marketCohortPreview.activeRowKey}
              previewLoadingKey={marketCohortPreview.previewLoadingKey}
            />
            {marketCohortPreview.previews.map((preview) => (
              <ReportMarketCohortPreviewCard
                key={preview.rowKey}
                preview={preview}
                tradesForReview={[]}
                tradesByIdForCohortStats={new Map()}
                overlayStats={marketCohortOverlayStatsByKey[preview.rowKey]}
                onUpsertOverlayStats={onUpsertCohortOverlayStats}
                onListFilterChange={marketCohortPreview.setListFilter}
                onClear={marketCohortPreview.clearPreview}
                tradesLoading={false}
                tradesError={null}
                report={report}
                brokerAccountCode={null}
                reportTimezone={undefined}
                candleTimeframe={candleTimeframeForReview}
                recordedTimezoneLabel={undefined}
                asOfEndMs={null}
                requireInstrumentSetup={false}
                demoJobId={demoJobId}
              />
            ))}
          </TabsContent>

          <TabsContent
            value="simulator"
            className="mt-2 data-[state=inactive]:hidden"
          >
            <DemoSimulatorUpsellPanel />
          </TabsContent>
        </Tabs>

        <DemoResultsPricingSection
          sectionRef={pricingSectionRef}
          surface="results_pricing"
        />
      </div>
    </DemoEntryAnalysisShell>
    {showMobileUpgradeBanner ? (
      <div
        data-demo-results-upgrade-sticky=""
        className="fixed inset-x-0 bottom-0 z-[120] border-t border-border bg-card/95 px-4 pt-3 shadow-[0_-8px_32px_rgba(15,23,42,0.12)] backdrop-blur-md md:hidden"
        style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
      >
        <div className="mx-auto flex w-full max-w-3xl items-center gap-3">
          <p
            className={cn(
              AUTH_FONT,
              "min-w-0 flex-1 text-sm font-medium leading-snug text-foreground"
            )}
          >
            Unlock the full Tessera platform
          </p>
          <Button
            type="button"
            variant="gradient"
            size="sm"
            className="shrink-0"
            onClick={scrollToPricingSection}
          >
            Upgrade
          </Button>
        </div>
      </div>
    ) : null}
    </>
  )
}
